export interface TaxonomyNode {
  id: number;
  name: string;
  children: TaxonomyNode[];
}

export interface TaxonomyTree {
  parents: TaxonomyNode[];
}

export type TaxonomyLayoutMode = 'tree' | 'flat';

export interface CategorySelection {
  selectedParentIds: Set<number>;
  selectedChildIds: Set<number>;
}

export const emptyCategorySelection = (): CategorySelection => ({
  selectedParentIds: new Set(),
  selectedChildIds: new Set(),
});

export const toCategoryFilterKey = (ids: number[]): string =>
  [...ids].sort((a, b) => a - b).join(',');

const INACTIVE_STATUSES = new Set(['inactive', 'in_active', 'disabled', 'deleted']);

const getCategoryId = (raw: Record<string, unknown>): number | null => {
  const id = raw.categoryId ?? raw.id;
  if (id == null || id === '') return null;
  const num = Number(id);
  return Number.isFinite(num) && num > 0 ? num : null;
};

const getChildSources = (raw: Record<string, unknown>): Record<string, unknown>[] => {
  const list = raw.categories ?? raw.children ?? [];
  return Array.isArray(list) ? (list as Record<string, unknown>[]) : [];
};

const isActiveNode = (node: Record<string, unknown> | null | undefined): boolean => {
  if (!node || getCategoryId(node) == null) return false;

  if (node.hidden === true || node.isHidden === true) return false;

  const status = node.status ?? node.active;
  if (status === false) return false;
  if (typeof status === 'string' && INACTIVE_STATUSES.has(status.toLowerCase())) {
    return false;
  }

  return true;
};

const toChildNode = (raw: Record<string, unknown>): TaxonomyNode | null => {
  if (!isActiveNode(raw)) return null;

  const id = getCategoryId(raw);
  if (id == null) return null;

  return {
    id,
    name: String(raw.name ?? raw.label ?? ''),
    children: [],
  };
};

const toParentNodeFromNested = (raw: Record<string, unknown>): TaxonomyNode | null => {
  if (!isActiveNode(raw)) return null;

  const id = getCategoryId(raw);
  if (id == null) return null;

  const children = getChildSources(raw)
    .map((child) => toChildNode(child))
    .filter((child): child is TaxonomyNode => child != null);

  return {
    id,
    name: String(raw.name ?? raw.label ?? ''),
    children,
  };
};

const toTaxonomyNode = (raw: Record<string, unknown>, depth = 0): TaxonomyNode | null => {
  if (!isActiveNode(raw)) return null;

  const id = getCategoryId(raw);
  if (id == null) return null;

  const childSources = depth < 1 ? getChildSources(raw) : [];
  const children = childSources
    .map((child) => toTaxonomyNode(child, depth + 1))
    .filter((child): child is TaxonomyNode => child != null);

  return {
    id,
    name: String(raw.name ?? raw.label ?? ''),
    children,
  };
};

const hasParentId = (raw: Record<string, unknown>): boolean => {
  const parentId = raw.parentId ?? raw.parentCategoryId;
  return parentId != null && parentId !== '' && Number(parentId) !== 0;
};

const extractRawCategoryList = (apiResponse: unknown): Record<string, unknown>[] => {
  const payload = (apiResponse as { data?: unknown })?.data ?? apiResponse;
  const data = payload as Record<string, unknown>;

  const list =
    data?.categories ??
    data?.categoryList ??
    data?.items ??
    (Array.isArray(payload) ? payload : []);

  return Array.isArray(list) ? (list as Record<string, unknown>[]) : [];
};

const buildTreeFromFlatList = (rawList: Record<string, unknown>[]): TaxonomyTree => {
  const nodeMap = new Map<number, TaxonomyNode>();

  rawList.forEach((raw) => {
    const node = toTaxonomyNode({ ...raw, categories: [], children: [] });
    if (node) {
      nodeMap.set(node.id, node);
    }
  });

  rawList.forEach((raw) => {
    if (!isActiveNode(raw)) return;

    const childId = getCategoryId(raw);
    if (childId == null) return;

    const child = nodeMap.get(childId);
    if (!child) return;

    if (!hasParentId(raw)) return;

    const parentId = Number(raw.parentId ?? raw.parentCategoryId);
    const parent = nodeMap.get(parentId);
    if (parent && !parent.children.some((item) => item.id === child.id)) {
      parent.children.push(child);
    }
  });

  const parents = rawList
    .filter((raw) => isActiveNode(raw) && !hasParentId(raw))
    .map((raw) => nodeMap.get(getCategoryId(raw)!))
    .filter((node): node is TaxonomyNode => node != null);

  return { parents };
};

const buildTreeFromNestedList = (rawList: Record<string, unknown>[]): TaxonomyTree => {
  const parents = rawList
    .filter((raw) => isActiveNode(raw) && !hasParentId(raw))
    .map((raw) => toParentNodeFromNested(raw))
    .filter((node): node is TaxonomyNode => node != null)
    .map((parent) => ({
      ...parent,
      children: parent.children.filter((child) => child.id !== parent.id),
    }));

  return { parents };
};

export const normalizeCategories = (apiResponse: unknown): TaxonomyTree => {
  const rawList = extractRawCategoryList(apiResponse);
  if (!rawList.length) {
    return { parents: [] };
  }

  const hasNestedChildren = rawList.some((raw) => getChildSources(raw).length > 0);

  return hasNestedChildren
    ? buildTreeFromNestedList(rawList)
    : buildTreeFromFlatList(rawList);
};

export const getTaxonomyLayoutMode = (tree: TaxonomyTree): TaxonomyLayoutMode => {
  const parentsWithChildren = tree.parents.filter((parent) => parent.children.length > 0);
  return parentsWithChildren.length >= 2 ? 'tree' : 'flat';
};

export const getFlatSubcategories = (tree: TaxonomyTree): TaxonomyNode[] => {
  const parentsWithChildren = tree.parents.filter((parent) => parent.children.length > 0);

  if (parentsWithChildren.length === 0) {
    return tree.parents;
  }
  if (parentsWithChildren.length === 1) {
    return parentsWithChildren[0].children;
  }
  return [];
};

export interface FlatTaxonomyFilterItem {
  id: number;
  name: string;
  displayName: string;
}

export const getFlatTaxonomyFilterItems = (tree: TaxonomyTree): FlatTaxonomyFilterItem[] => {
  const items = getFlatSubcategories(tree);
  const parentsWithChildren = tree.parents.filter((parent) => parent.children.length > 0);
  const singleParent =
    parentsWithChildren.length === 1 ? parentsWithChildren[0] : undefined;

  return items.map((item) => {
    const isChild = singleParent?.children.some((child) => child.id === item.id) ?? false;
    return {
      id: item.id,
      name: item.name,
      displayName: isChild ? `${item.name} ${singleParent!.name}`.trim() : item.name,
    };
  });
};

export const getInitialExpandedParentIds = (
  tree: TaxonomyTree,
  layoutMode: TaxonomyLayoutMode,
): number[] => {
  if (layoutMode !== 'tree') return [];
  return tree.parents.map((parent) => parent.id);
};

export const parseCategoryTypeIdsFromQuery = (value: unknown): number[] => {
  if (value == null || value === '') return [];

  const rawValues = Array.isArray(value) ? value : [value];
  const ids = rawValues
    .flatMap((entry) => String(entry).split(','))
    .map((entry) => Number(entry.trim()))
    .filter((id) => Number.isFinite(id) && id > 0);

  return [...new Set(ids)];
};

const isParentFullySelected = (
  parent: TaxonomyNode,
  selectedParentIds: Set<number>,
  selectedChildIds: Set<number>,
): boolean => {
  if (selectedParentIds.has(parent.id)) return true;
  if (!parent.children.length) return false;
  return parent.children.every((child) => selectedChildIds.has(child.id));
};

export const toApiCategoryTypeIds = (
  tree: TaxonomyTree,
  selectedParentIds: Set<number>,
  selectedChildIds: Set<number>,
): number[] => {
  const apiIds: number[] = [];

  tree.parents.forEach((parent) => {
    if (isParentFullySelected(parent, selectedParentIds, selectedChildIds)) {
      apiIds.push(parent.id);
      return;
    }

    parent.children.forEach((child) => {
      if (selectedChildIds.has(child.id)) {
        apiIds.push(child.id);
      }
    });
  });

  return [...new Set(apiIds)];
};

export const fromApiCategoryTypeIds = (
  tree: TaxonomyTree,
  apiIds: number[],
): CategorySelection => {
  const selectedParentIds = new Set<number>();
  const selectedChildIds = new Set<number>();
  const apiIdSet = new Set(apiIds);

  tree.parents.forEach((parent) => {
    if (apiIdSet.has(parent.id)) {
      selectedParentIds.add(parent.id);
      parent.children.forEach((child) => selectedChildIds.add(child.id));
      return;
    }

    parent.children.forEach((child) => {
      if (apiIdSet.has(child.id)) {
        selectedChildIds.add(child.id);
      }
    });

    if (
      parent.children.length > 0 &&
      parent.children.every((child) => selectedChildIds.has(child.id))
    ) {
      selectedParentIds.add(parent.id);
    }
  });

  return { selectedParentIds, selectedChildIds };
};

export const getParentCheckboxState = (
  parent: TaxonomyNode,
  selectedParentIds: Set<number>,
  selectedChildIds: Set<number>,
): { checked: boolean; indeterminate: boolean } => {
  if (selectedParentIds.has(parent.id)) {
    return { checked: true, indeterminate: false };
  }

  if (!parent.children.length) {
    return { checked: false, indeterminate: false };
  }

  const selectedCount = parent.children.filter((child) => selectedChildIds.has(child.id)).length;
  if (selectedCount === 0) {
    return { checked: false, indeterminate: false };
  }

  if (selectedCount === parent.children.length) {
    return { checked: true, indeterminate: false };
  }

  return { checked: false, indeterminate: true };
};

export const loadCategoryTypeIdsFromSetting = (
  settingInfo: Record<string, unknown> | null | undefined,
): number[] => {
  const stored = settingInfo?.categoryTypeIds;
  if (!Array.isArray(stored)) return [];

  return stored
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && id > 0);
};

/**
 * Reads category filter IDs from the checkout portal query string.
 *
 * Uses `typeIds` (comma-separated) to match production deep-link behavior. The portal
 * applies it once on load and removes it from the URL like other bootstrap params
 * (pickupDate, locationId, etc.). UI category selection does not write back to the URL.
 *
 * Do not use `categoryTypeId` here — that name was introduced in UAT only and is stripped
 * without applying; internal/API naming uses categoryTypeIds elsewhere.
 */
export const parseCategoryTypeIdsFromUrl = (
  qsObject: Record<string, unknown>,
): number[] | null => {
  if (qsObject.typeIds != null && qsObject.typeIds !== '') {
    return parseCategoryTypeIdsFromQuery(qsObject.typeIds);
  }

  return null;
};
